import React, { useState } from 'react'
import { EmailOutlined, HomeOutlined, IosShare, Laptop, Person, PhoneOutlined, Visibility, Share as ShareIcon } from '@mui/icons-material'
import { API_URL } from '../../../api'
import { Avatar, Box, Button, IconButton, Tooltip, Typography, Card, CardContent, Chip, Stack, useTheme, alpha } from '@mui/material'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import ShareProfileDialog from '../../../Components/Dialog/ShareProfile'
import config from 'config'
import { getAvatarUrl } from '../../../utils/imageUtils'
// import { LinkedIn, Twitter, Web, Instagram, Facebook } from '@mui/icons-material';

const MainBox = (props) => {
  const { tutor } = props
  const [showNumber, setShowNumber] = useState(false)
  const [openShareProfileDialog, setOpenShareProfileDialog] = useState(false)
  const theme = useTheme()

  const navigate = useNavigate()
  const {
    userId, isGuest, status
  } = useSelector((state) => state.main);
  const { user, avatar, /*address,*/ price, title, email, phone, level, gender, qualifications, online, faceToFace, address, _id } = tutor
  const { firstName, lastName } = user
  const qualificationsArray = Object.keys(qualifications).filter(qualification => qualifications[qualification] > 0 && qualification !== 'Student')
  const costText = value => `£${value}`;
  const userAvatar = getAvatarUrl(avatar, gender)

  const handleCloseSharePopup = () => {
    setOpenShareProfileDialog(false)
  }
  const handleShowSharePopup = () => {
    setOpenShareProfileDialog(true)
  }

  return (
    <Card sx={{ 
      borderRadius: 3,
      boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.08)}`,
      border: `1px solid ${theme.palette.divider}`,
      overflow: 'hidden'
    }}>
      <CardContent sx={{ p: 0 }}>
        {/* Header Section */}
        <Box sx={{ p: 3, pb: 2 }}>
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
            {/* Avatar */}
            <Avatar
              alt="user avatar"
              src={userAvatar}
              sx={{ 
                width: { xs: 110, sm: 130, md: 150 },
                height: { xs: 110, sm: 130, md: 150 },
                border: `4px solid ${theme.palette.background.paper}`,
                boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.1)}`,
                backgroundColor: userAvatar ? 'transparent' : alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                fontSize: { xs: '2rem', sm: '2.25rem', md: '2.75rem' },
                fontWeight: 600,
                '& .MuiAvatar-img': {
                  objectFit: 'cover'
                }
              }}
            >
              {!userAvatar && `${firstName?.[0] || ''}${lastName?.[0] || ''}`}
            </Avatar>

            {/* Main Info */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.text.primary, mb: 0.5 }}>
                    {firstName} {lastName && lastName[0]}.
                  </Typography>
                  <Typography variant="h6" sx={{ 
                    color: theme.palette.primary.main, 
                    fontWeight: 600,
                    textTransform: 'capitalize',
                    mb: 1
                  }}>
                    {title}
                  </Typography>
                </Box>

                {/* Price and Share */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                  <Typography variant="h4" sx={{ 
                    fontWeight: 700, 
                    color: theme.palette.primary.main,
                    lineHeight: 1.2
                  }}>
                    {costText(price[0])}/hr
                  </Typography>
                  
                  <Tooltip title="Share Profile" arrow>
                    <IconButton
                      onClick={handleShowSharePopup}
                      sx={{
                        color: theme.palette.text.secondary,
                        '&:hover': {
                          color: theme.palette.primary.main,
                          backgroundColor: alpha(theme.palette.primary.main, 0.08)
                        }
                      }}
                    >
                      <ShareIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {/* Contact Info */}
              <Stack direction="row" spacing={3} flexWrap="wrap" sx={{ mb: 2 }}>
                {email && email.length > 0 && (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1
                  }}>
                    <EmailOutlined fontSize="small" sx={{ color: theme.palette.text.secondary }} />
                    <Typography variant="body2" sx={{ 
                      color: theme.palette.text.secondary
                    }}>
                      {userId && !isGuest ? email : userId && isGuest && status === 1 ? email : '******@gmail.com'}
                    </Typography>
                  </Box>
                )}
                {phone && phone.length > 0 && (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1
                  }}>
                    <PhoneOutlined fontSize="small" sx={{ color: theme.palette.text.secondary }} />
                    <Typography variant="body2" sx={{ 
                      color: theme.palette.text.secondary
                    }}>
                      {userId && !isGuest ? phone : userId && isGuest && status === 1 ? phone : '*******000'}
                    </Typography>
                  </Box>
                )}
                {(email || phone) && !userId && (
                  <Tooltip title="Login to see Email & Phone" arrow>
                    <IconButton 
                      size="small" 
                      onClick={() => navigate('/signin')}
                      sx={{
                        color: theme.palette.text.secondary,
                        '&:hover': {
                          color: theme.palette.primary.main,
                          backgroundColor: alpha(theme.palette.primary.main, 0.08)
                        }
                      }}
                    >
                      <Visibility fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>

              {/* Tutoring Levels */}
              {level && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ 
                    color: theme.palette.text.secondary,
                    mb: 1
                  }}>
                    <strong>Tutoring:</strong> {Object.keys(level).filter(levelKey => level[levelKey]).join(', ')}
                  </Typography>
                </Box>
              )}

              {/* Availability Icons */}
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                {online && (
                  <Chip
                    icon={<Laptop fontSize="small" />}
                    label="Online"
                    size="small"
                    variant="outlined"
                    sx={{
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.secondary,
                      backgroundColor: 'transparent',
                      fontWeight: 500
                    }}
                  />
                )}
                {faceToFace && (
                  <Chip
                    icon={<Person fontSize="small" />}
                    label="In-person"
                    size="small"
                    variant="outlined"
                    sx={{
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.secondary,
                      backgroundColor: 'transparent',
                      fontWeight: 500
                    }}
                  />
                )}
              </Box>

              {/* Qualifications */}
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {qualificationsArray.map((qualification, index) => (
                  <Chip
                    key={index}
                    label={qualification}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.secondary,
                      backgroundColor: 'transparent',
                      fontWeight: 500,
                      '& .MuiChip-label': {
                        px: 1.5
                      }
                    }}
                  />
                ))}
              </Stack>
            </Box>
          </Box>
        </Box>
      </CardContent>

      {/* Share Profile Dialog */}
      {openShareProfileDialog && (
        <ShareProfileDialog 
          open={openShareProfileDialog} 
          handleClose={handleCloseSharePopup} 
          tutorId={_id}
          tutorName={`${firstName} ${lastName && lastName[0]}.`}
        />
      )}
    </Card>
  );
};

export default MainBox;
